import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, TrendingUp, TrendingDown } from 'lucide-react';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { useFetchCryptoHoldings, useFetchCryptoPrices } from '@cryptoHooks';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PageHeaderSkeleton, AccountCardSkeleton } from '@/components/LoadingSkeletons';

function formatCurrency(value: number, currency = 'USD') {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(value);
}

/**
 * CryptoHoldingDetailPage — reads :holdingId from useParams and displays
 * full details for a single crypto holding.
 * Route: /investments/crypto/:holdingId
 */
export default function CryptoHoldingDetailPage() {
  const { holdingId } = useParams<{ holdingId: string }>();

  const { data: holdings = [], isLoading } = useFetchCryptoHoldings();

  const holding = useMemo(
    () => holdings.find((h) => h.id === holdingId),
    [holdings, holdingId]
  );

  const symbols = useMemo(
    () => (holding ? [holding.symbol] : []),
    [holding]
  );

  const { data: prices = [], isLoading: pricesLoading } = useFetchCryptoPrices(symbols);

  if (isLoading) {
    return (
      <ErrorBoundary onReset={() => window.location.reload()}>
        <div className="space-y-6" aria-busy="true" aria-live="polite" data-testid="loading-skeleton">
          <PageHeaderSkeleton />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => <AccountCardSkeleton key={i} />)}
          </div>
          <AccountCardSkeleton />
        </div>
      </ErrorBoundary>
    );
  }

  if (!holding) {
    return (
      <ErrorBoundary onReset={() => window.location.reload()}>
        <div className="space-y-4" data-testid="not-found-state">
          <p className="text-muted-foreground">Crypto holding not found.</p>
          <Button asChild variant="outline">
            <Link to="/investments/crypto" data-testid="back-link">
              <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
              Back to Crypto Portfolio
            </Link>
          </Button>
        </div>
      </ErrorBoundary>
    );
  }

  const priceData = prices.find((p) => p.symbol === holding.symbol);
  const currentPrice = priceData?.price ?? 0;
  const change24h = priceData?.change_24h ?? 0;
  const currentValue = holding.amount * currentPrice;
  const profitLoss = currentValue - holding.cost_basis;
  const profitLossPct =
    holding.cost_basis > 0 ? (profitLoss / holding.cost_basis) * 100 : 0;
  const isProfit = profitLoss >= 0;
  const isPositive24h = change24h >= 0;

  const currency = holding.currency ?? 'USD';

  return (
    <ErrorBoundary onReset={() => window.location.reload()}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              asChild
              aria-label="Back to Crypto Portfolio"
              className="min-h-[44px] min-w-[44px]"
              data-testid="back-button"
            >
              <Link to="/investments/crypto">
                <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              </Link>
            </Button>
            <div>
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl" data-testid="holding-name">
                {holding.name}
              </h1>
              <p className="text-sm text-muted-foreground sm:text-base" data-testid="holding-symbol">
                {holding.symbol}
              </p>
            </div>
          </div>
        </div>

        {/* Metrics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Current Price */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Current Price</CardTitle>
            </CardHeader>
            <CardContent>
              {pricesLoading ? (
                <div className="h-7 w-24 animate-pulse rounded bg-muted" data-testid="price-skeleton" />
              ) : (
                <>
                  <p className="text-2xl font-bold" data-testid="current-price">
                    {formatCurrency(currentPrice, currency)}
                  </p>
                  <div
                    className={`flex items-center gap-1 text-sm mt-1 ${isPositive24h ? 'text-green-600' : 'text-red-600'}`}
                    data-testid="change-24h"
                  >
                    {isPositive24h ? (
                      <TrendingUp className="h-3 w-3" aria-hidden="true" />
                    ) : (
                      <TrendingDown className="h-3 w-3" aria-hidden="true" />
                    )}
                    {isPositive24h ? '+' : ''}{change24h.toFixed(2)}% (24h)
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Current Value */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Current Value</CardTitle>
            </CardHeader>
            <CardContent>
              {pricesLoading ? (
                <div className="h-7 w-24 animate-pulse rounded bg-muted" />
              ) : (
                <p className="text-2xl font-bold" data-testid="current-value">
                  {formatCurrency(currentValue, currency)}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Cost Basis */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Cost Basis</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold" data-testid="cost-basis">
                {formatCurrency(holding.cost_basis, currency)}
              </p>
            </CardContent>
          </Card>

          {/* Profit / Loss */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Profit / Loss</CardTitle>
            </CardHeader>
            <CardContent>
              {pricesLoading ? (
                <div className="h-7 w-24 animate-pulse rounded bg-muted" />
              ) : (
                <>
                  <p
                    className={`text-2xl font-bold ${isProfit ? 'text-green-600' : 'text-red-600'}`}
                    data-testid="profit-loss-absolute"
                  >
                    {isProfit ? '+' : ''}{formatCurrency(profitLoss, currency)}
                  </p>
                  <p
                    className={`text-sm mt-1 ${isProfit ? 'text-green-600' : 'text-red-600'}`}
                    data-testid="profit-loss-pct"
                  >
                    {isProfit ? '+' : ''}{profitLossPct.toFixed(2)}%
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Holding Details */}
        <Card>
          <CardHeader>
            <CardTitle>Holding Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Name</p>
                <p className="font-medium" data-testid="detail-name">{holding.name}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Symbol</p>
                <Badge variant="secondary" data-testid="detail-symbol">{holding.symbol}</Badge>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Amount</p>
                <p className="font-medium" data-testid="detail-amount">
                  {holding.amount.toLocaleString(undefined, { maximumFractionDigits: 8 })} {holding.symbol}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Currency</p>
                <p className="font-medium">{currency}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Wallet Details */}
        <Card>
          <CardHeader>
            <CardTitle>Wallet</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Provider</p>
                <p className="font-medium" data-testid="wallet-provider">
                  {holding.wallet?.provider_name ?? '—'}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Wallet Name</p>
                <p className="font-medium" data-testid="wallet-name">
                  {holding.wallet?.name ?? '—'}
                </p>
              </div>
              {holding.wallet?.address && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Wallet Address</p>
                  <p className="font-medium font-mono text-xs break-all" data-testid="wallet-address">
                    {holding.wallet.address}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Back link (bottom) */}
        <div>
          <Button asChild variant="outline">
            <Link to="/investments/crypto" data-testid="back-link-bottom">
              <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
              Back to Crypto Portfolio
            </Link>
          </Button>
        </div>
      </div>
    </ErrorBoundary>
  );
}
